import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import Spinner from 'react-bootstrap/Spinner'
import { Link } from 'react-router-dom'
import { apiRequest, ensureCompanyInfo, getAccessToken, showKAlert } from '../helpers'
import { t } from '../locale'
import { Invoice, PaymentMethod } from '../react-app-env'
import { fetchPlans } from '../service/user'
import { cancelSubscription, reactivateSubscription, startTrial } from '../service/subscription'
import countryList from 'react-select-country-list'
import { getSubscriptionYearPriceId } from '../service/stripeConfig'
import { formatPrice } from '../service/currency'

function Billing() {
  const accessToken = getAccessToken()

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [paymentCustomer, setPaymentCustomer] = useState('')

  const [selectedType, setSelectedType] = useState('')

  const [subscription, setSubscription] = useState(false)
  // True only for a real paid subscription (not a used-up trial). `subscription`
  // above doubles as "show the subscribe screen", so it can't gate the button.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false)
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState('')

  const [invoices, setInvoices] = useState<Invoice[]>([])

  const [trial, setTrial] = useState(false)
  const [points, setPoints] = useState(0)
  const [days, setDays] = useState(0)

  const [address, setAddress] = useState('')
  const [address2, setAddress2] = useState('')
  const [city, setCity] = useState('')
  const [stateRegion, setStateRegion] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [country, setCountry] = useState('')
  const [vatNumber, setVatNumber] = useState('')

  const countries = useMemo(
    () =>
      countryList()
        .getData()
        .sort((a, b) => t(a.label).localeCompare(t(b.label), 'de')),
    []
  )

  const [loader, setLoader] = useState(false)
  const [plan, setPlan] = useState<{ price?: number; points?: number }>({})

  useEffect(() => {
    if (!paymentCustomer) return

    apiRequest(`${process.env.REACT_APP_API_URL}/stripe/invoices/${paymentCustomer}`, 'GET')
      .then(response => setInvoices(response))
      .catch(err => console.error('Failed to fetch invoices:', err))

    if (paymentCustomer) {
      apiRequest(`${process.env.REACT_APP_API_URL}/stripe/customer-address`, 'POST', {
        customerId: paymentCustomer,
      })
        .then(res => {
          setAddress(res.address || '')
          setAddress2(res.address2 || '')
          setCity(res.city || '')
          setStateRegion(res.state || '')
          setPostalCode(res.postalCode || '')
          setCountry(res.country || '')
        })
        .catch(err => {
          console.error('Failed to fetch customer address:', err)
        })
    }
  }, [paymentCustomer])

  const fetchPaymentMethods = useCallback(async () => {
    apiRequest(`${process.env.REACT_APP_API_URL}/stripe/get-payment-methods`, 'POST', {
      customerId: paymentCustomer,
    }).then(data => setPaymentMethods(data.paymentMethods || []))
  }, [paymentCustomer])

  useEffect(() => {
    if (paymentCustomer) {
      fetchPaymentMethods()
    }
  }, [paymentCustomer, fetchPaymentMethods])

  const checkSubscription = useCallback(async () => {
    try {
      const res = await apiRequest(`${process.env.REACT_APP_API_URL}/subscription/check`, 'POST', {
        subdomain: window.subdomain,
      })

      if (res.status) {
        if (res.type === 'subscribed') {
          setSelectedType(res.subscriptionType)
          setPaymentCustomer(res.customerId)
          setSubscription(true)
          setHasActiveSubscription(true)
          setCancelAtPeriodEnd(!!res.cancelAtPeriodEnd)
          setCurrentPeriodEnd(res.currentPeriodEnd || '')
          localStorage.setItem('subscriptionId', res.subscriptionId)
        } else if (res.type === 'trial') {
          if (res.customerId) {
            setPaymentCustomer(res.customerId)
          }
          if (res.pointsLeft <= 8 || res.daysLeft <= 0) {
            setSubscription(true)
            setTrial(false)
          } else {
            setTrial(true)
            setPoints(res.pointsLeft)
            setDays(res.daysLeft)
          }
        }
      }

      await getPlans()
    } catch (error) {
      console.error('Error checking subscription:', error)
    }
  }, [])

  useEffect(() => {
    checkSubscription()
  }, [checkSubscription])

  const getPlans = async () => {
    const fetchedPlan = await fetchPlans()
    setPlan(fetchedPlan)
  }

  const fetchCheckoutUrl = useCallback(
    async (priceId: string, customer: string) => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/subscription/create-checkout-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken,
            companyInfo: await ensureCompanyInfo(),
            priceId,
            paymentCustomer: customer,
          }),
        }
      )
      if (!res.ok) throw new Error(`Checkout session request failed: ${res.status}`)
      const data = await res.json()
      if (typeof data.url !== 'string' || !data.url) {
        throw new Error('Checkout session response is missing url.')
      }
      return data.url
    },
    [accessToken]
  )

  const getOrCreatePaymentCustomer = async (subscription: boolean): Promise<string | null> => {
    if (paymentCustomer) return paymentCustomer

    const email = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail') || ''
    if (!email && !subscription) {
      showKAlert('danger', t('paymentCustomerEmailNotFound'))
      return null
    }

    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_API_URL}/stripe/create-payment-customer`,
        'POST',
        { email }
      )

      if (response?.customerId) {
        setPaymentCustomer(response.customerId)
        return response.customerId
      }
      showKAlert('danger', t('createPaymentCustomerError'))
      return null
    } catch (err) {
      console.error('Error creating payment customer:', err)
      showKAlert('danger', t('createPaymentCustomerError'))
      return null
    }
  }

  const redirectToStripePortal = async () => {
    // if (!accessToken) {
    //     showKAlert('danger', 'firstSubscribe')
    //     window.scrollTo(0, 0)
    //     return
    // }

    try {
      // First: Get or create payment customer
      const customerId = await getOrCreatePaymentCustomer(false)
      if (!customerId) {
        window.scrollTo(0, 0)
        return
      }

      // Then: Redirect to Stripe portal
      const response = await apiRequest(
        `${process.env.REACT_APP_API_URL}/stripe/create-customer-portal-session`,
        'POST',
        {
          accessToken: accessToken,
          customerId: customerId,
        }
      )

      window.location.href = response.url
    } catch (err) {
      console.error('Error redirecting to Stripe portal:', err)
      showKAlert('danger', t('requestProcessingError'))
      window.scrollTo(0, 0)
    }
  }

  const updatePaymentInfo = async () => {
    // if (!accessToken) {
    //     showKAlert('danger', 'firstSubscribe')
    //     window.scrollTo(0, 0)
    //     return
    // }

    try {
      // First: Get or create payment customer
      const customerId = await getOrCreatePaymentCustomer(false)
      if (!customerId) {
        window.scrollTo(0, 0)
        return
      }

      // Then: Update billing address
      const response = await apiRequest(
        `${process.env.REACT_APP_API_URL}/stripe/update-customer-address`,
        'POST',
        {
          customerId: customerId,
          address,
          address2,
          city,
          state: stateRegion,
          postalCode,
          country,
        }
      )

      if (response.success === true) {
        showKAlert('success', t('billingAddressUpdated'))
      } else {
        showKAlert('danger', t('billingAddressUpdateError'))
      }

      window.scrollTo(0, 0)
    } catch (err) {
      console.error('Failed to update billing address:', err)
      showKAlert('danger', t('billingAddressUpdateError'))
      window.scrollTo(0, 0)
    }
  }

  const formatPaymentMethodLabel = (pm: PaymentMethod): string => {
    if (pm.type === 'card' && pm.card) {
      return `${pm.card.brand.toUpperCase()} •••• ${pm.card.last4}`
    }

    const details = pm[pm.type]
    const identifier = details?.last4
      ? `•••• ${details.last4}`
      : details?.payer_email || details?.email || ''
    const typeLabel = pm.type
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')

    return identifier ? `${typeLabel} ${identifier}` : typeLabel
  }

  const handleDelete = async (paymentMethodId: string) => {
    apiRequest(`${process.env.REACT_APP_API_URL}/stripe/detach-payment-methods`, 'POST', {
      paymentMethodId,
    }).then(() => fetchPaymentMethods())
  }

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('confirmCancelSubscription'))) return

    setLoader(true)
    try {
      const res = await cancelSubscription()
      if (res.ok) {
        setCancelAtPeriodEnd(true)
        if (res.currentPeriodEnd) setCurrentPeriodEnd(res.currentPeriodEnd)
        showKAlert('success', t('subscriptionCancelScheduled'))
      } else {
        showKAlert('danger', t('subscriptionChangeError'))
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err)
      showKAlert('danger', t('subscriptionChangeError'))
    } finally {
      setLoader(false)
    }
  }

  const handleReactivateSubscription = async () => {
    setLoader(true)
    try {
      const res = await reactivateSubscription()
      if (res.ok) {
        setCancelAtPeriodEnd(false)
        showKAlert('success', t('subscriptionReactivated'))
      } else {
        showKAlert('danger', t('subscriptionChangeError'))
      }
    } catch (err) {
      console.error('Failed to reactivate subscription:', err)
      showKAlert('danger', t('subscriptionChangeError'))
    } finally {
      setLoader(false)
    }
  }

  const handleSubscribeClick = async (priceId: string) => {
    if (trial && !window.confirm(t('confirmEndTrialSubscribe'))) return

    setSelectedType('team')
    setLoader(true)

    try {
      const customerId = await getOrCreatePaymentCustomer(true)
      if (!customerId) return
      setPaymentCustomer(customerId)

      const url = await fetchCheckoutUrl(priceId, customerId)
      if (url) window.location.assign(url)
    } catch (err) {
      console.error('Failed to start subscription checkout: ', err)
      showKAlert('danger', t('requestProcessingError'))
    } finally {
      setLoader(false)
    }
  }

  const handleStartTrial = async () => {
    setLoader(true)
    try {
      const res = await startTrial()
      if (res.ok) {
        showKAlert('success', t('trialStarted'))
        await checkSubscription()
      } else {
        showKAlert('danger', t('trialNotAvailable'))
      }
    } catch (err) {
      console.error('Failed to start trial:', err)
      showKAlert('danger', t('trialStartError'))
    } finally {
      setLoader(false)
    }
  }

  const handleDownload = (invoicePdfUrl: string) => {
    if (!invoicePdfUrl) return
    window.open(invoicePdfUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <div className=''>
        {loader && (
          <div className='position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75 z-3'>
            <Spinner
              animation='border'
              variant='primary'
              style={{ width: '3rem', height: '3rem' }}
            />
          </div>
        )}
        <Row>
          <div className='col-md-2 bg-white'>
            <div
              className='offcanvas position-relative pt-md-3 visible'
              id='sidebarMenu'
              aria-labelledby='sidebarMenuLabel'
            >
              <div className='offcanvas-body d-md-flex flex-column p-4 p-lg-3'>
                <Link to='/' className='d-inline-block p-5'>
                  <img
                    src='/KYC_logo_black.png'
                    className='img-fluid'
                    style={{ height: 72, width: 'auto' }}
                    alt='DATA-Security | KYC'
                  />
                </Link>

                <p className='fs-4 my-3'>{t('billingManagement')}</p>
                <p className='my-3'>{t('signedAsAdmin')}</p>
                <p className='my-3'>
                  {t('firstSentence')}
                  <br />
                  {t('secondSentence')}
                </p>

                <Link to='https://data-security.one/de/datenschutzhinweise' target='_blank'>
                  {t('termsOfService')}
                </Link>
                <br />
                <Link to='/' className='my-3'>
                  ← {t('backToApplication')}
                </Link>

                <hr className='my-3' />
              </div>
            </div>
          </div>

          <main className='col ms-sm-auto pt-5 px-md-5 w-full min-h-screen bg-gray-100'>
            {trial && (
              <div
                className='alert alert-info d-flex align-items-start flex-wrap flex-md-nowrap align-items-center pt-4 px-md-5'
                role='alert'
              >
                <div className='me-2'>
                  <i className='bi bi-lightning-charge-fill text-primary fs-4'></i>
                </div>
                <div>
                  <h5 className='alert-heading mb-1'>{t('trialPeriodActive')}</h5>
                  <p className='mb-0'>
                    {t('currentTrialMode')}
                    <br />
                    <strong>{days}</strong> {days !== 1 ? t('days') : t('day')} {t('remainingOr')}
                    <strong> {points}</strong> {points !== 1 ? t('points') : t('point')}{' '}
                    {t('pointsLeft')}.
                  </p>
                  <br />
                  {t('trialExpiryInfo')}
                  <br />
                  <br />
                  <strong>{t('endTrialSubscribeTitle')}</strong> {t('endTrialSubscribeInfo')}
                </div>
              </div>
            )}

            {!trial && subscription && (
              <div
                className='alert alert-info d-flex align-items-start flex-wrap flex-md-nowrap align-items-center p-4 px-md-5 gap-3'
                role='alert'
              >
                <div className='me-2'>
                  <i className='bi bi-stars text-primary fs-3'></i>
                </div>
                <div>
                  <h5 className='alert-heading mb-2'>{t('allSet')}</h5>
                  <p className='mb-1'>{t('subscriptionIsActive')}</p>
                  <p className='mb-1'>
                    <strong>{t('plan')}:</strong> {t(selectedType)}
                  </p>
                  <p className='mb-1'>
                    <strong>{t('period')}:</strong> {t('yearly')}
                  </p>
                  {hasActiveSubscription &&
                    (cancelAtPeriodEnd ? (
                      <>
                        <p className='mb-2 text-danger'>
                          {t('subscriptionEndsOn')}{' '}
                          <strong>
                            {currentPeriodEnd
                              ? new Date(currentPeriodEnd).toLocaleDateString('de')
                              : ''}
                          </strong>
                          . {t('subscriptionAccessUntilEnd')}
                        </p>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          onClick={handleReactivateSubscription}
                        >
                          {t('resumeSubscription')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className='mb-2 text-muted small'>{t('thankForSub')}</p>
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={handleCancelSubscription}
                        >
                          {t('cancelSubscription')}
                        </Button>
                      </>
                    ))}
                </div>
                <button
                  className='btn btn-outline-primary position-absolute end-0 bottom-0 m-3'
                  onClick={() => setSubscription(false)}
                >
                  {t('manageSubscription')}
                </button>
              </div>
            )}

            {!subscription && (
              <>
                <div className='bg-gray-100 pb-4 mb-4'>
                  {!hasActiveSubscription && (
                    <>
                      <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-4 px-md-5'>
                        <h3 className='px-6 sm:px-0 text-2xl font-semibold text-gray-700'>
                          {t('Subscribe')}
                        </h3>
                      </div>
                      <Row className='g-3 mb-5 px-md-5'>
                        <div id='subscriptionPlan' className='col-12'>
                          <Card className='p-2'>
                            <Card.Body>
                              <h4 className='fw-bold'>DS | KYC</h4>
                              <p>
                                {plan.price != null ? formatPrice(plan.price) : ''} (ex VAT)&nbsp;/{' '}
                                {t('yearly')}
                              </p>
                              <div className='mt-3 max-w-xl text-sm text-secondary'>
                                {t('teamYearFirst')} <br />
                                {t('teamYearSecond')} <br />
                                {t('teamYearThird')} <br />
                                <br />
                              </div>
                              <ul className='list-unstyled'>
                                <li className='d-flex align-items-center mb-2'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='20'
                                    height='20'
                                    fill='#4ade80'
                                    className='me-2'
                                    viewBox='0 0 24 24'
                                  >
                                    <circle cx='12' cy='12' r='12' fill='#4ade80' />
                                    <path
                                      d='M17 8.5L10.5 15 7 11.5'
                                      stroke='#fff'
                                      strokeWidth='2'
                                      fill='none'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                  <span className='text-secondary'>
                                    {plan.points} {t('points')}
                                  </span>
                                </li>
                                <li className='d-flex align-items-center mb-2'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='20'
                                    height='20'
                                    fill='#4ade80'
                                    className='me-2'
                                    viewBox='0 0 24 24'
                                  >
                                    <circle cx='12' cy='12' r='12' fill='#4ade80' />
                                    <path
                                      d='M17 8.5L10.5 15 7 11.5'
                                      stroke='#fff'
                                      strokeWidth='2'
                                      fill='none'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                  <span className='text-secondary'>
                                    {t('services')}: KYC Check, DS Identity
                                  </span>
                                </li>
                                <li className='d-flex align-items-center'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='20'
                                    height='20'
                                    fill='#4ade80'
                                    className='me-2'
                                    viewBox='0 0 24 24'
                                  >
                                    <circle cx='12' cy='12' r='12' fill='#4ade80' />
                                    <path
                                      d='M17 8.5L10.5 15 7 11.5'
                                      stroke='#fff'
                                      strokeWidth='2'
                                      fill='none'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    />
                                  </svg>
                                  <span className='text-secondary'>
                                    {t('additionalSeatNotice')}
                                  </span>
                                </li>
                              </ul>
                            </Card.Body>
                            <Card.Footer className='bg-white border-top-0 text-end pt-0 pb-2 pe-2'>
                              <Button
                                onClick={() => handleSubscribeClick(getSubscriptionYearPriceId())}
                              >
                                {t('Subscribe')}
                              </Button>
                              {!trial && (
                                <div className='mt-2'>
                                  <Button
                                    variant='link'
                                    size='sm'
                                    className='text-muted link-underline-opacity-0 p-0'
                                    onClick={handleStartTrial}
                                  >
                                    {t('startTrialOption')}
                                  </Button>
                                </div>
                              )}
                            </Card.Footer>
                          </Card>
                        </div>
                      </Row>
                    </>
                  )}
                  <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-4 px-md-5'>
                    <h1 className='h3'>{t('paymentMethod')}</h1>
                  </div>
                  <Row className='g-3 mb-5 px-md-5'>
                    <div className='col-12'>
                      <Card className='p-2'>
                        <Card.Body>
                          {paymentMethods.length === 0 ? (
                            <p>{t('noSavedPayment')}</p>
                          ) : (
                            <>
                              {paymentMethods.map(pm => (
                                <ul
                                  key={pm.id}
                                  className='border border-gray-100 rounded-md divide-y divide-gray-200'
                                >
                                  <div className='w-0 flex-1 flex items-center'>
                                    <svg
                                      className='flex-shrink-0 h-5 w-5 text-gray-600'
                                      xmlns='http://www.w3.org/2000/svg'
                                      fill='none'
                                      viewBox='0 0 24 24'
                                      strokeWidth='1.5'
                                      stroke='currentColor'
                                    >
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        d='M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z'
                                      ></path>
                                    </svg>
                                    <div className='ml-3 flex-1 w-0 truncate'>
                                      {formatPaymentMethodLabel(pm)}
                                      {pm.type === 'card' && pm.card && (
                                        <div>
                                          <span className='text-xs text-gray-500'>
                                            Expires {pm.card.exp_month}/{pm.card.exp_year}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className='pe-2 pb-2 text-end'>
                                      <button
                                        onClick={() => handleDelete(pm.id)}
                                        className='text-gray-400 hover:text-red-600 transition p-2 rounded-full'
                                        title='Remove'
                                      >
                                        <svg
                                          className='h-5 w-5'
                                          xmlns='http://www.w3.org/2000/svg'
                                          fill='none'
                                          viewBox='0 0 24 24'
                                          strokeWidth='1.5'
                                          stroke='currentColor'
                                        >
                                          <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0'
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </ul>
                              ))}
                            </>
                          )}
                        </Card.Body>
                        <Card.Footer className='bg-white border-top-0 pt-0 pb-2 pe-2 text-end'>
                          <Button
                            className='btn-primary'
                            onClick={() => {
                              redirectToStripePortal()
                            }}
                          >
                            {t('addPaymentMethod')}
                          </Button>
                        </Card.Footer>
                      </Card>
                    </div>
                  </Row>
                  <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-4 px-md-5'>
                    <h1 className='h3'>{t('paymentInfo')}</h1>
                  </div>
                  <div className='row g-3 mb-5 px-md-5'>
                    <div className='col-12'>
                      <div className='card p-2'>
                        <div className='card-body'>
                          <div id='payment_info' style={{ display: 'block' }}>
                            <p>{t('yourBillingAddress')} </p>
                          </div>
                          <form id='update_payment_info'>
                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='address' className='col-form-label'>
                                  {t('addressLine1')}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='address'
                                  className='form-control'
                                  aria-describedby='address'
                                  value={address}
                                  onChange={e => setAddress(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='address2' className='col-form-label'>
                                  {t('addressLine2')}{' '}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='address2'
                                  className='form-control'
                                  aria-describedby='address2'
                                  value={address2}
                                  onChange={e => setAddress2(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='city' className='col-form-label'>
                                  {t('city')}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='city'
                                  className='form-control'
                                  aria-describedby='city'
                                  value={city}
                                  onChange={e => setCity(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='state' className='col-form-label'>
                                  {t('stateCountry')}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='state'
                                  className='form-control'
                                  aria-describedby='state'
                                  value={stateRegion}
                                  onChange={e => setStateRegion(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='postal_code' className='col-form-label'>
                                  {t('zipPostalCode')}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='postal_code'
                                  className='form-control'
                                  aria-describedby='postal_code'
                                  value={postalCode}
                                  onChange={e => setPostalCode(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='country' className='col-form-label'>
                                  {t('country')}
                                </label>
                              </div>
                              <div className='col'>
                                <select
                                  name='country'
                                  id='country'
                                  className='form-select'
                                  value={country}
                                  onChange={e => setCountry(e.target.value)}
                                >
                                  <option value=''>--</option>
                                  {countries.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {t(option.label)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className='row g-3 align-items-center mb-3'>
                              <div className='col-3'>
                                <label htmlFor='vat' className='col-form-label'>
                                  {t('vatNumber')}
                                </label>
                              </div>
                              <div className='col'>
                                <input
                                  type='text'
                                  id='vat'
                                  className='form-control'
                                  aria-describedby='vat'
                                  value={vatNumber}
                                  onChange={e => setVatNumber(e.target.value)}
                                />
                              </div>
                            </div>
                          </form>
                        </div>
                        <div
                          className='card-footer bg-white border-top-0 pt-0 pb-2 pe-2 text-end'
                          id='update_payment_info_footer'
                        >
                          <Button
                            className='btn btn-primary'
                            id='update_payment_button1'
                            onClick={() => {
                              updatePaymentInfo()
                            }}
                          >
                            {t('updatePaymentInfo')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-4 px-md-5'>
                    <h1 className='h3'>{t('invoices')}</h1>
                  </div>
                  <div className='g-3 px-md-5 mb-4'>
                    <div className='col-12'>
                      <Card className='p-2'>
                        <Card.Body>
                          {invoices && invoices.length > 0 ? (
                            invoices.map(invoice => (
                              <Row>
                                <div className='col-6'>
                                  <p>{new Date(invoice.created * 1000).toLocaleDateString()}</p>
                                </div>
                                <Col className='text-center'>
                                  <div className='badge bg-primary-subtle text-title fw-normal rounded-pill'>
                                    {t('paid')}
                                  </div>
                                </Col>
                                <div className='col text-end'>
                                  <i
                                    className='bi bi-download'
                                    onClick={() => handleDownload(invoice.invoice_pdf)} // Stilizuj ikonu
                                  />
                                </div>
                              </Row>
                            ))
                          ) : (
                            <p>Loading invoices...</p>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </Row>
      </div>
    </>
  )
}

export default Billing
